import { lazy, Suspense, type ReactNode } from 'react';
import { createBrowserRouter, Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/lib/auth';

const LandingPage = lazy(() => import('@/features/landing/landing-page'));
const AuthLayout = lazy(() => import('@/features/auth/auth-layout'));
const LoginPage = lazy(() => import('@/features/auth/login-page'));
const RegisterPage = lazy(() => import('@/features/auth/register-page'));
const ForgotPasswordPage = lazy(() => import('@/features/auth/forgot-password-page'));
const ResetPasswordPage = lazy(() => import('@/features/auth/reset-password-page'));
const VerifyEmailPage = lazy(() => import('@/features/auth/verify-email-page'));
const AcceptInvitePage = lazy(() => import('@/features/auth/accept-invite-page'));
const OAuthCallbackPage = lazy(() => import('@/features/auth/oauth-callback-page'));
const OnboardingPage = lazy(() => import('@/features/onboarding/onboarding-page'));
const DashboardLayout = lazy(() => import('@/features/dashboard/dashboard-layout'));
const OverviewPage = lazy(() => import('@/features/dashboard/overview-page'));
const ProductsPage = lazy(() => import('@/features/products/products-page'));
const ProductDetailPage = lazy(() => import('@/features/products/product-detail-page'));
const MediaPage = lazy(() => import('@/features/media/media-page'));
const AnalyticsPage = lazy(() => import('@/features/analytics/analytics-page'));
const IntegrationsPage = lazy(() => import('@/features/integrations/integrations-page'));
const EmbedPage = lazy(() => import('@/features/integrations/embed-page'));
const TeamPage = lazy(() => import('@/features/team/team-page'));
const BillingPage = lazy(() => import('@/features/billing/billing-page'));
const SettingsPage = lazy(() => import('@/features/settings/settings-page'));
const AdminLayout = lazy(() => import('@/features/admin/admin-layout'));
const AdminOverviewPage = lazy(() => import('@/features/admin/admin-overview-page'));
const AdminOrgsPage = lazy(() => import('@/features/admin/admin-orgs-page'));
const AdminUsersPage = lazy(() => import('@/features/admin/admin-users-page'));
const ShowroomPage = lazy(() => import('@/features/showroom/showroom-page'));
const NotFoundPage = lazy(() => import('@/features/landing/not-found-page'));
const LegalPage = lazy(() => import('@/features/landing/legal-page'));

function FullPageSpinner() {
  return (
    <div className="grid min-h-screen place-items-center bg-background">
      <div className="size-10 animate-spin rounded-full border-[3px] border-border border-t-primary" />
    </div>
  );
}

function Protected({ children }: { children: ReactNode }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return <FullPageSpinner />;
  if (!user) return <Navigate to="/auth/login" state={{ from: location.pathname }} replace />;
  return <>{children}</>;
}

function AdminOnly({ children }: { children: ReactNode }) {
  const { user, loading } = useAuth();
  if (loading) return <FullPageSpinner />;
  if (!user?.isSuperAdmin) return <Navigate to="/dashboard" replace />;
  return <>{children}</>;
}

const wrap = (node: ReactNode) => <Suspense fallback={<FullPageSpinner />}>{node}</Suspense>;

export const router = createBrowserRouter([
  { path: '/', element: wrap(<LandingPage />) },
  { path: '/terms', element: wrap(<LegalPage kind="terms" />) },
  { path: '/privacy', element: wrap(<LegalPage kind="privacy" />) },
  {
    path: '/auth',
    element: wrap(<AuthLayout />),
    children: [
      { path: 'login', element: wrap(<LoginPage />) },
      { path: 'register', element: wrap(<RegisterPage />) },
      { path: 'forgot-password', element: wrap(<ForgotPasswordPage />) },
      { path: 'reset-password', element: wrap(<ResetPasswordPage />) },
      { path: 'verify-email', element: wrap(<VerifyEmailPage />) },
      { path: 'accept-invite', element: wrap(<AcceptInvitePage />) },
      { path: 'callback', element: wrap(<OAuthCallbackPage />) },
    ],
  },
  { path: '/onboarding', element: wrap(<Protected><OnboardingPage /></Protected>) },
  {
    path: '/dashboard',
    element: wrap(<Protected><DashboardLayout /></Protected>),
    children: [
      { index: true, element: wrap(<OverviewPage />) },
      { path: 'products', element: wrap(<ProductsPage />) },
      { path: 'products/:productId', element: wrap(<ProductDetailPage />) },
      { path: 'media', element: wrap(<MediaPage />) },
      { path: 'analytics', element: wrap(<AnalyticsPage />) },
      { path: 'integrations', element: wrap(<IntegrationsPage />) },
      { path: 'embed', element: wrap(<EmbedPage />) },
      { path: 'team', element: wrap(<TeamPage />) },
      { path: 'billing', element: wrap(<BillingPage />) },
      { path: 'settings/*', element: wrap(<SettingsPage />) },
    ],
  },
  {
    path: '/admin',
    element: wrap(<Protected><AdminOnly><AdminLayout /></AdminOnly></Protected>),
    children: [
      { index: true, element: wrap(<AdminOverviewPage />) },
      { path: 'orgs', element: wrap(<AdminOrgsPage />) },
      { path: 'users', element: wrap(<AdminUsersPage />) },
    ],
  },
  { path: '/s/:slug', element: wrap(<ShowroomPage />) },
  { path: '*', element: wrap(<NotFoundPage />) },
]);
